
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Briefcase, Home, Car, CreditCard, PiggyBank, 
  Wrench, Sailboat, Plane, DollarSign 
} from "lucide-react";
import PartnersCarousel from "@/components/products/PartnersCarousel";
import SeoMetadata from "@/components/common/SeoMetadata";
import { useEffect } from "react";
import { saveSeoMetadata } from "@/utils/seoUtils";

// SEO Keywords for Swedish Products Page
const seoKeywords = [
  "låneprodukter", "jämföra lån", "olika lån", 
  "billiga lån", "bostadslån", "konsumentkrediter", 
  "billån", "skuldkonsolidering", "renoveringslån",
  "båtlån", "semesterlån", "företagslån", "flexibel kredit"
];

const SwedishProductsPage = () => {
  const isMobile = useIsMobile();
  
  const seoTitle = "Alla Låneprodukter | Lainasampo.fi";
  const seoDescription = "Utforska alla låneprodukter som erbjuds av Lainasampo.fi. Hitta rätt lån för dina behov enkelt.";
  const pageUrl = "https://lainasampo.fi/swedish/products";
  
  // Save SEO metadata to Supabase
  useEffect(() => {
    saveSeoMetadata("/swedish/products", {
      title: seoTitle,
      description: seoDescription,
      keywords: seoKeywords,
    });
  }, []);
  
  return (
    <>
      <SeoMetadata
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl={pageUrl}
        language="sv"
      />
      
      <div className="bg-white">
        <div className="container mx-auto max-w-6xl px-4">
          {isMobile && (
            <div className="py-4">
              <Link to="/swedish/apply">
                <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-4 rounded-lg shadow-md text-base flex items-center justify-center">
                  <span className="text-lg">Ansök om lån</span>
                </Button>
              </Link>
            </div>
          )}
          
          {/* Hero Section with blue background */}
          <section className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-12 rounded-lg mb-10">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-3xl md:text-4xl font-bold mb-4">Våra Låneprodukter</h1>
              <p className="text-xl md:text-2xl mb-4 text-blue-100">
                Vi erbjuder ett omfattande utbud av låneprodukter för att möta dina olika ekonomiska behov. Jämför alternativen och hitta den perfekta lösningen för dig.
              </p>
              {!isMobile && (
                <Link to="/swedish/apply">
                  <Button className="bg-white text-blue-600 px-8 py-3 text-lg rounded-md hover:bg-blue-50 transition-colors">
                    Jämför Låneerbjudanden
                  </Button>
                </Link>
              )}
            </div>
          </section>
          
          {/* Products Grid */}
          <div className="py-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <ProductCard 
                title="Konsumentkrediter" 
                description="Flexibel finansiering för vardagliga inköp och oväntade utgifter."
                icon={<CreditCard className="h-10 w-10 text-blue-600" />}
                link="/swedish/apply"
              />
              <ProductCard 
                title="Bolån" 
                description="Prisvärda bostadsfinansiering med konkurrenskraftiga räntor."
                icon={<Home className="h-10 w-10 text-blue-600" />}
                link="/swedish/apply"
              />
              <ProductCard 
                title="Billån" 
                description="Skräddarsydda finansieringslösningar för att köpa din drömbil."
                icon={<Car className="h-10 w-10 text-blue-600" />}
                link="/swedish/apply"
              />
              <ProductCard 
                title="Skuldkonsolidering" 
                description="Förenkla din ekonomi genom att kombinera flera lån till en enda betalning."
                icon={<PiggyBank className="h-10 w-10 text-blue-600" />}
                link="/swedish/apply"
              />
              <ProductCard 
                title="Renoveringslån" 
                description="Finansiera dina hemmaprojekt med flexibla återbetalningsalternativ."
                icon={<Wrench className="h-10 w-10 text-blue-600" />}
                link="/swedish/apply"
              />
              <ProductCard 
                title="Båtlån" 
                description="Särskild finansiering för köp och underhåll av båtar."
                icon={<Sailboat className="h-10 w-10 text-blue-600" />}
                link="/swedish/apply"
              />
              <ProductCard 
                title="Semesterlån" 
                description="Finansiera din drömsemester med enkla återbetalningsvillkor."
                icon={<Plane className="h-10 w-10 text-blue-600" />}
                link="/swedish/apply"
              />
              <ProductCard 
                title="Företagslån" 
                description="Finansiella lösningar för entreprenörer och växande företag."
                icon={<Briefcase className="h-10 w-10 text-blue-600" />}
                link="/swedish/apply"
              />
              <ProductCard 
                title="Flexibel kredit" 
                description="Revolverande kreditlinje som alltid finns tillgänglig när du behöver den."
                icon={<DollarSign className="h-10 w-10 text-blue-600" />}
                link="/swedish/apply"
              />
            </div>
          </div>
          
          {/* Partners Section - Using PartnersCarousel with Swedish language */}
          <PartnersCarousel altTitle="Våra Pålitliga Partners" language="sv" />
          
          {isMobile && (
            <div className="py-6 mb-8">
              <Link to="/swedish/apply">
                <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-4 rounded-lg shadow-md text-base flex items-center justify-center">
                  <span className="text-lg">Ansök Nu</span>
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

interface ProductCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  link: string;
}

const ProductCard = ({ title, description, icon, link }: ProductCardProps) => {
  return (
    <Link to={link}>
      <Card className="h-full transition-all duration-200 hover:shadow-md hover:scale-[1.02]">
        <CardContent className="p-6">
          <div className="mb-4">
            {icon}
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">{title}</h3>
          <p className="text-gray-600">{description}</p>
        </CardContent>
      </Card>
    </Link>
  );
};

export default SwedishProductsPage;
